import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { Session, User } from '@supabase/supabase-js'
import { usePostHog } from '@/composables/use-posthog'
import { useSupabase } from '@/composables/use-supabase.ts'
import { useOrganizationsStore } from '@/stores/organizations.ts'
import { useSuperAdmin } from '@/composables/use-super-admin'
import { useRouter } from 'vue-router'

export const useUserStore = defineStore('user', () => {
  const { supabase } = useSupabase()
  const { identifyUser, resetUser, trackEvent } = usePostHog()
  const router = useRouter()

  /* STATE VARIABLES */
  const user = ref<User | null>(null)
  const isLoggedIn = ref<boolean>(false)

  /* EVENT LISTENERS */
  supabase.auth.onAuthStateChange(async (event, session) => {
    if (event === 'SIGNED_OUT' && !session) {
      await cleanupStores()
      resetUser()
      router.push({ name: 'login' })
    }

    if (event === 'SIGNED_IN' && session) {
      if (user.value?.id !== session.user.id) {
        await cleanupStores()
      }
      identifyUser(session.user.id, {
        email: session.user.email,
        email_verified: session.user.email_confirmed_at ? true : false,
        created_at: session.user.created_at,
        last_sign_in_at: session.user.last_sign_in_at,
      })
    }

    setDataFromSession(session)
  })

  const setDataFromSession = (session: Session | null) => {
    user.value = session?.user ?? null
    isLoggedIn.value = session ? !session?.user?.is_anonymous : false
  }

  /* ACTIONS */
  const checkSession = async () => {
    const { data: { session }, error } = await supabase.auth.getSession()

    if (error) {
      throw error
    }

    setDataFromSession(session)
    return session
  }

  const getAccessToken = async () => {
    const { data: { session }, error } = await supabase.auth.getSession()

    if (error) {
      throw error
    }

    const minimumTimeToExpireInSeconds = 5 * 60
    // `expires_in` is the token lifetime at issue time (typically 3600s), not
    // the time remaining now, so it never drops below the threshold. Compare
    // `expires_at` (UNIX seconds) against the wall clock instead.
    const nowSec = Math.floor(Date.now() / 1000)
    const expiresAt = session?.expires_at ?? 0
    if (expiresAt && expiresAt - nowSec < minimumTimeToExpireInSeconds) {
      // Force refresh the token to ensure backend has a token that will be valid for some time
      const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession()

      if (refreshError) {
        throw refreshError
      }

      return refreshed?.session?.access_token ?? null
    }

    return session?.access_token ?? null
  }

  const logout = async () => {
    const user_id = user.value?.id
    const { error } = await supabase.auth.signOut({ scope: 'local' })

    if (error) {
      throw error
    }

    // No `email`: it stays a person property (`identifyUser` above), and an
    // event property would copy it onto every login and logout event.
    trackEvent('user_logout', {
      user_id: user_id,
    })

    return null
  }

  const signInWithOtp = async (email: string) => {
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
      },
    })

    if (error) {
      throw error
    }

    return data
  }

  const signInFlow = async (email: string) => {

    const data = await signInWithOtp(email)

    return { data }
  }

  const verifyOtp = async (email: string, token: string) => {
    const { data: { session }, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    })

    if (error) {
      throw error
    }

    return session
  }

  const verifyOtpFlow = async (email: string, token: string) => {
    return await verifyOtp(email, token)
  }

  const signInWithOAuth = async (provider: 'google' | 'apple') => {
    const { data, error } = await supabase.auth.signInWithOAuth({ provider })

    if (error) {
      throw error
    }

    trackEvent('user_login', {
      user_id: user.value?.id,
      provider: provider,
    })

    return data
  }

  /* UTILITY FUNCTIONS */
  const cleanupStores = async () => {
    const { resetOrganizations } = useOrganizationsStore()
    resetOrganizations()

    const { resetSuperAdmin } = useSuperAdmin()
    resetSuperAdmin()
  }

  return {
    // state variables
    user,
    isLoggedIn,
    // actions
    checkSession,
    getAccessToken,
    logout,
    signInFlow,
    verifyOtpFlow,
    signInWithOAuth
  }
})
